import { useEffect, useState } from "react";
import axios from 'axios';
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from 'react-fullscreen-loading';
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {FaEdit, FaTrashAlt} from "react-icons/fa";
import { Tooltip } from 'react-tooltip';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import DataTable from 'react-data-table-component';
import { Button } from "react-bootstrap";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import { Can, useAuth } from "../../../authentication/AuthContext";
import TruncatedText, { TruncatedTextTooltip, DateCell, COLUMN_WIDTHS } from "../../../Components/TruncatedText/TruncatedText";

export default function ActiveServices() {
  const { can } = useAuth();
  const navigate = useNavigate();

  const [TableData,setTableData]= useState([]);
  const [screenloader,setScreenloader] = useState(true);
  const [TotalRecords,setTotalRecords] = useState();
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy,setSortBy] = useState(null);
  const [sortOrder,setSortOrder] = useState(null);
  const [pending, setPending] = useState(true);

  // filters
  const initialFilters = {
    name: { operator: '', value: '' }
  };
  const [filters, setFilters] = useState(initialFilters);
  const [name, setName] = useState("");
  
  const handleFiltersChange = (key, value, operator) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: {
        operator: operator,
        value: value
      }
    }));
  };

  const handleFiltersReset = () =>{
    setFilters(initialFilters);
    setName("");
  }

  const handleEnterKeyPress = (e) => {
    var value = e.target.value;
    if(value !== ""){
      value = `%${value}%`
    }
    if (e.key === 'Enter') {
      handleFiltersChange(e.target.id, value, 'LIKE')    
    }
  };

  const columns = [
    { 
      name: "Actions", 
      // Hidden entirely rather than left as an empty column: omit drops the
      // header as well as the cells.
      omit: !(can(['admin.services.update', 'admin.services.delete'])),
      sortable: false, 
      cell: row => (
        <div>
          <Can permission="admin.services.update">
            <FaEdit
              data-tooltip-id={`edit-${row.id}`}
              data-tooltip-content="Edit Service"
              className="edit-icon"
              onClick={() => {
                navigate(`/Admin/Services/edit/${row.id}`, {
                  replace: false,
                });
              }}
            />
            <Tooltip id={`edit-${row.id}`} />
          </Can>

          <Can permission="admin.services.delete">
            <FaTrashAlt
              data-tooltip-id={`delete-${row.id}`}
              data-tooltip-content="Delete Service"
              className="edit-icon"
              onClick={() => handleDelete(row.id)}
            />
            <Tooltip id={`delete-${row.id}`} />
          </Can>
        </div>
      )
    },
    {
      name: 'ID',
      selector: row => (row.id),
      sortField: "id",
      sortable: true,
      cell: row=>(<div style={{ whiteSpace: 'pre-wrap' }}>{row.id}</div>),
    },
    {
      name: 'Name',
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: row => (row.name),
      sortField: "name",
      sortable: true,
      cell: row => <TruncatedText text={row.name} maxWidth={COLUMN_WIDTHS.name} />,
    },
    {
      name: 'Slug',
      minWidth: `${COLUMN_WIDTHS.slug}px`,
      selector: row => row.slug,
      sortField: "slug",
      sortable: true,
      cell: row => <TruncatedText text={row.slug} maxWidth={COLUMN_WIDTHS.slug} mono />,
    },
    {
      name: 'Active',
      selector: row => row.active,
      sortField: "active",
      sortable: true,
      cell: row => <div style={{ whiteSpace: 'pre-wrap' }}>{row.active ? "yes" : "no"}</div>,
    },
    {
      name: 'Active Modules',
      selector: row => row.modules,
      sortField: "modules",
      sortable: true,
      cell: row => {
        const activeModules = row.modules ? row.modules.filter(module => module.active).map(module => module.name) : [];
        const fullText = activeModules.join(', ');
        const displayText = fullText.length > 20 ? fullText.substring(0, 20) + '...' : fullText;
        return (
          <div 
            data-tooltip-id={`modules-${row.id}`}
            data-tooltip-content={fullText}
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {displayText}
            <Tooltip id={`modules-${row.id}`} />
          </div>
        );
      },
    },
    {
      name: 'Created At',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.created_at,
      sortField: "created_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.created_at)} />,
    },
    {
      name: 'Updated At',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.updated_at,
      sortField: "updated_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.updated_at)} />,
    }
  ];

  const getServices = async () => {
    try {
      const formattedFilters = Object.keys(filters)
        .filter(key => filters[key].value) // Filter out falsy values
        .map((key, index) => ({
          [`filters[${index}][column]`]: key,
          [`filters[${index}][operator]`]: filters[key].operator,
          [`filters[${index}][value]`]: filters[key].value,
        }))
        .reduce((acc, obj) => ({ ...acc, ...obj }), {});

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/services`, {
        params: {
          limit: pageSize,
          page: page,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...formattedFilters
        },
        headers: {
          'access-token': localStorage.getItem('accessToken'),
        },
      });
      setScreenloader(false);
      setPending(false);
      if (response.data.status) {
        const total = response.data.meta.pagination.total;
        setTotalRecords(total);
        setTableData(response.data.services);
      } else {
        toast.error('Something Went Wrong');
        console.log(response.data);
      }
    } catch (error) {
      setScreenloader(false);
      setPending(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error('Something Went Wrong');
      }
    }
  };

  useEffect(() => {
    setPending(true);
    getServices();
  }, [filters, page, pageSize, sortBy, sortOrder]);

  const handleDelete = async (id) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this service?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              const response = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/services/${id}`, {
                headers: {
                  'access-token': localStorage.getItem('accessToken'),
                },
              });
              
              if (response.data.status) {
                toast.success('Service deleted successfully');
                getServices(); // Refresh the table
              } else {
                toast.error('Failed to delete service');
              }
            } catch (error) {
              if (error.response?.data?.message) {
                toast.error(error.response.data.message);
              } else {
                toast.error('Something went wrong');
              }
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };
return (
  <div>
    <AdminDashboardLayout />
    <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
    <div className='content-page-bg'>
      <div className='content-page'>
        <div className='content'>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>Services</h2>
            <Can permission="admin.services.create">
            <Button
              className="btn admin-btn"
              onClick={() => navigate('/Admin/Services/new')} 
            >
              Add New Service
            </Button>
            </Can>
          </div>
          <div className="filters row">
            <div className="col-md-3 mb-3">
              <div className="input-group">
                <input
                    type="text"
                    className="form-control"
                    id="s.name"
                    placeholder='Name'
                    value={name}
                    onChange={e =>{setName(e.target.value);}}
                    onKeyDown={handleEnterKeyPress}
                />
                {name && (
                  <span
                    className="input-group-text"
                    role="button"
                    onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 's.name', value: name } })}
                  >
                    Apply
                  </span>
                )}
              </div>
            </div>
          </div>
          <DataTable
            //data
            columns={columns}
            data={TableData}
            //style
            dense
            direction="auto"
            highlightOnHover
            noHeader
            persistTableHead
            pointerOnHover
            responsive
            subHeaderAlign="right"
            subHeaderWrap
            progressPending={pending}
            progressComponent={<CustomLoader />}
            // conditionalRowStyles={conditionalRowStyles}
            //pagination
            pagination
            paginationServer 
            paginationPerPage={pageSize} 
            paginationTotalRows={TotalRecords} 
            paginationRowsPerPageOptions={[10,20,50,100,150]}
            onChangePage={(page) => setPage(page)} 
            onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {setPageSize(currentRowsPerPage); setPage(currentPage)}} 
            sortServer
            onSort={(column, sortDirection) => {setSortBy(column.sortField); setSortOrder(sortDirection);}}
          />
        </div>
      </div>
    </div>
    <TruncatedTextTooltip />
  </div>
);
}
